import apiClient from './client'
import { apiEndpoints } from '@/config/api'

/**
 * Authentication API Module
 * 
 * Handles all authentication-related API operations.
 */

export const authApi = {
  /**
   * Register new customer account
   * @param {Object} data - Registration data
   * @param {string} data.first_name - First name
   * @param {string} data.last_name - Last name
   * @param {string} data.email - Email address
   * @param {string} data.phone - Phone number (optional)
   * @param {string} data.password - Password
   * @param {string} data.password_confirmation - Password confirmation
   * @returns {Promise} User data with token
   */
  async register(data) {
    const response = await apiClient.post(apiEndpoints.auth.register, data)
    return response.data
  },

  /**
   * Login customer
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - Email address
   * @param {string} credentials.password - Password
   * @returns {Promise} User data with token
   */
  async login(credentials) {
    const response = await apiClient.post(apiEndpoints.auth.login, credentials)
    return response.data
  },

  /**
   * Logout current user
   * @returns {Promise} Logout confirmation
   */
  async logout() {
    const response = await apiClient.post(apiEndpoints.auth.logout)
    return response.data
  },

  /**
   * Revalidate the current authenticated user.
   * Shop customer tokens are long-lived Sanctum tokens, so there is no
   * dedicated refresh endpoint to rotate them server-side.
   * @returns {Promise} Current user data
   */
  async refreshToken() {
    return this.getUser()
  },

  /**
   * Get authenticated user data
   * @returns {Promise} Current user data
   */
  async getUser() {
    const response = await apiClient.get(apiEndpoints.auth.me)
    return response.data
  },

  /**
   * Send password reset link
   * @param {string} email - Customer email address
   * @returns {Promise} API response
   */
  async forgotPassword(email) {
    const response = await apiClient.post(apiEndpoints.auth.forgotPassword, { email })
    return response.data
  },

  /**
   * Reset customer password
   * @param {Object} data - Reset payload
   * @returns {Promise} API response
   */
  async resetPassword(data) {
    const response = await apiClient.post(apiEndpoints.auth.resetPassword, data)
    return response.data
  },
}

export default authApi
